//deliveries-page.tsx

import React, { useState, useContext, useEffect, useRef } from "react";
import Page from "../components/page";
import {
  Button,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { DateTime } from "luxon";
import LoadingBackdrop from "../components/loading-backdrop";
import { UserContext, ErrorContext } from "../components/app-routes";
import { formatCurrency } from "./currency";
import { deliveriesApi, Delivery } from "../services/api/deliveries";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import mixpanel from "mixpanel-browser";
import { UpgradePlanDialog } from "../components/upgrade-plan-dialog";
import { useHistory } from "react-router-dom";
import { unitSystemLabel } from "./crops-page";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { Grower } from "../models/grower";
import { styled } from "@mui/material/styles";
import ResponsiveActionBar from "../components/responsive-action-bar";
import { getCustomLabel } from "../utils/custom-labels";

function CustomerOrdersTable(props: {
  deliveriesByCustomer: Delivery[];
  grower: Grower;
}) {
  const delivery = props.deliveriesByCustomer[0];

  return (
    <TableContainer component={Paper} sx={{ mt: 2, minWidth: 650 }}>
      <Table size="small">
        <TableBody>
          <TableRow sx={{ backgroundColor: "#d9d9d9" }}>
            <TableCell></TableCell>
            <TableCell sx={{ fontWeight: "bold" }} colSpan={2}>
              {" "}
              {delivery.customerName}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: "bold" }} colSpan={3}>
              Order Total:{" "}
              {formatCurrency(
                props.deliveriesByCustomer.reduce(
                  (total, delivery) =>
                    total + delivery.price * delivery.quantity,
                  0,
                ),
                props.grower.CountryISO,
              )}
            </TableCell>
          </TableRow>
          {delivery.customerAddressLine1 && (
            <TableRow sx={{ backgroundColor: "#d9d9d9" }}>
              <TableCell></TableCell>
              <TableCell sx={{ fontWeight: "bold" }} colSpan={5}>
                {delivery.customerAddressLine1}
              </TableCell>
            </TableRow>
          )}
          {delivery.customerDeliveryInstructions && (
            <TableRow sx={{ backgroundColor: "#d9d9d9" }}>
              <TableCell></TableCell>
              <TableCell sx={{ fontWeight: "bold" }} colSpan={5}>
                {delivery.customerDeliveryInstructions}
              </TableCell>
            </TableRow>
          )}
          <TableRow sx={{ backgroundColor: "#efefef" }}>
            <TableCell sx={{ width: "30px" }}></TableCell>
            <TableCell align="left" sx={{ fontWeight: "bold" }}>
              Product
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold" }}>
              Size
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold" }}>
              Quantity
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold" }}>
              Price
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold" }}>
              Total
            </TableCell>
          </TableRow>
          {props.deliveriesByCustomer
            .slice()
            .sort((a, b) => a.productName.localeCompare(b.productName))
            .map((d) => (
              <TableRow
                key={`${d.customerId}-${d.productName}-${d.productSize}`}
              >
                <TableCell>
                  <CheckBoxOutlineBlankIcon />
                </TableCell>
                <TableCell>{d.productName}</TableCell>
                <TableCell align="center">
                  {d.productSize}
                  {d.productWeight
                    ? ` (${d.productWeight} ${unitSystemLabel(props.grower)})`
                    : ``}
                </TableCell>
                <TableCell align="center">{d.quantity}</TableCell>
                <TableCell align="center">
                  {formatCurrency(d.price, props.grower.CountryISO)}
                </TableCell>
                <TableCell align="center">
                  {formatCurrency(
                    d.quantity * d.price,
                    props.grower.CountryISO,
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function DeliveriesPage() {
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(DateTime.now());
  const [deliveries, setDeliveries] = useState<Delivery[] | undefined>(
    undefined,
  );
  const [openUpgradePlanDialog, setOpenUpgradePlanDialog] = useState(false);
  const context = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const grower = context.grower;
  let groupedDeliveries: { [key: string]: Delivery[] } | undefined = undefined;
  const history = useHistory();
  const isMounted = useRef(true);
  const actionButtonStyle = {
    minHeight: 50, // matches Tasks
    paddingY: 0.75, // vertical padding (≈ 6px)
    paddingX: 2, // horizontal padding (≈ 16px)
  };

  if (!grower) {
    throw new Error("Invalid context");
  }

  if (deliveries) {
    groupedDeliveries = groupBy(deliveries, "customerDeliveryRoute");
  }

  useEffect(() => {
    mixpanel.track("View Deliveries Page");
  }, []);

  useEffect(() => {
    isMounted.current = true;

    if (
      grower.Plan !== "business" &&
      grower.Plan !== "pro" &&
      isMounted.current
    ) {
      setOpenUpgradePlanDialog(true);
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (grower.Plan !== "business" && grower.Plan !== "pro") {
      return;
    }
    isMounted.current = true;

    async function getData() {
      setBusy(true);
      try {
        const result = await deliveriesApi.getAll(date);
        if (isMounted.current) setDeliveries(result);
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }

    getData();

    return () => {
      isMounted.current = false;
    };
  }, [date]);

  const handleDateChange = (date: DateTime | null) => {
    if (date === null) {
      return;
    }
    setDate(date);
  };

  const handleExportPDF = () => {
    async function post() {
      setBusy(true);
      try {
        await deliveriesApi.getPdf(date);

        mixpanel.track("Export Deliveries Pdf");
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }
    post();
  };

  const handleExportMapsCSV = () => {
    if (!deliveries) {
      return;
    }

    mixpanel.track("Export Maps CSV");

    const seen = new Set<number>(); // Track customerIds we've already exported
    const rows = [
      [
        "Name",
        "Address",
        "City",
        "Post Code",
        "Province/State",
        "Notes",
        "Route",
      ],
    ];

    deliveries.forEach((d) => {
      if (seen.has(d.customerId)) {
        return;
      }
      seen.add(d.customerId);

      rows.push([
        d.customerName || "",
        d.customerAddressLine1 || "",
        d.customerLocality || "",
        d.customerPostalCode || "",
        d.customerRegion || "",
        d.customerDeliveryInstructions || "",
        d.customerDeliveryRoute || "",
      ]);
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((e) => e.map((v) => `"${v}"`).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `seedleaf_deliveries_addresses_${date.toFormat("yyyy-LL-dd")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onClickCancelUpgradePlan = () => {
    history.replace("/home");
  };

  const DeliveriesActionBar = styled("div")(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),

    // Desktop: normal row, left aligned
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
    },

    // Mobile: stack rows
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",

      ".mobile-top-row": {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        gap: theme.spacing(1),
      },

      ".mobile-bottom-row": {
        width: "100%",
      },
    },
  }));

  return (
    <Page title={getCustomLabel(grower ?? null, "deliveries")}>
      <ResponsiveActionBar
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,

          // Desktop (same as Tasks)
          "> *": {
            order: 0,
          },

          // Mobile layout
          "@media (max-width: 600px)": {
            flexDirection: "column",
            alignItems: "flex-start",

            ".mobile-top-row": {
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
              gap: 8,
            },

            ".mobile-middle-row": {
              display: "flex",
              gap: 8,
              width: "100%",
            },

            ".mobile-bottom-row": {
              width: "100%",
              marginTop: 4,
            },
          },
        }}
      >
        {/* ===== MOBILE + DESKTOP: DATE + ARROWS ===== */}
        <Box className="mobile-top-row">
          <MobileDatePicker
            format="MMM d, yyyy"
            value={date}
            onChange={handleDateChange}
            closeOnSelect={true}
            slotProps={{
              toolbar: { hidden: true },
              textField: {
                variant: "outlined",
                inputProps: { readOnly: true },
                sx: { width: 170 },
              },
            }}
          />

          <IconButton
            onClick={() => setDate(date.minus({ days: 1 }))}
            sx={{ pl: 2 }}
          >
            <ArrowBackIosIcon />
          </IconButton>

          <IconButton
            onClick={() => setDate(date.plus({ days: 1 }))}
            sx={{ pr: 1 }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {/* ===== TODAY + EXPORT PDF (middle row on mobile) ===== */}
        <Box className="mobile-middle-row" sx={{ gap: 2 }}>
          <Button
            size="large"
            color="secondary"
            variant="contained"
            onClick={() => setDate(DateTime.now().startOf("day"))}
            sx={{ ...actionButtonStyle, mr: 2, mt: 1 }}
          >
            Today
          </Button>

          <Button
            size="large"
            color="secondary"
            variant="contained"
            onClick={handleExportPDF}
            sx={{ ...actionButtonStyle, mt: 1 }}
          >
            Export PDF
          </Button>
        </Box>

        {/* ===== EXPORT MAPS CSV (always its own row) ===== */}
        <Box className="mobile-bottom-row">
          <Button
            size="large"
            color="secondary"
            variant="contained"
            onClick={handleExportMapsCSV}
            sx={{ ...actionButtonStyle, width: 222, mt: 1 }}
          >
            Export Maps CSV
          </Button>
        </Box>
      </ResponsiveActionBar>
      {groupedDeliveries && (
        <Box pt={2}>
          {Object.keys(groupedDeliveries).length > 0 ? (
            <>
              {Object.keys(groupedDeliveries)
                .sort((a, b) => a.localeCompare(b))
                .map((route) => {
                  if (!groupedDeliveries) {
                    throw new Error("Invalid state");
                  }

                  const deliveriesByCustomer: {
                    [key: string]: Delivery[];
                  } = groupBy(groupedDeliveries[route], "customerId");

                  const customerIdsSorted = Object.keys(
                    deliveriesByCustomer,
                  ).sort((a, b) =>
                    deliveriesByCustomer[a][0].customerName.localeCompare(
                      deliveriesByCustomer[b][0].customerName,
                    ),
                  );

                  return (
                    <React.Fragment key={route}>
                      <Typography variant="h5" sx={{ mt: 2 }} color="primary">
                        {route ? <>Route: {route}</> : <>No Route</>}
                      </Typography>

                      {customerIdsSorted.map((customerId) => (
                        <CustomerOrdersTable
                          key={customerId}
                          deliveriesByCustomer={
                            deliveriesByCustomer[customerId]
                          }
                          grower={grower}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
            </>
          ) : (
            <Typography variant="body1">
              No deliveries on{" "}
              {date.toLocaleString({ month: "long", day: "numeric" })}.
            </Typography>
          )}
        </Box>
      )}
      <LoadingBackdrop open={busy} />
      <UpgradePlanDialog
        open={openUpgradePlanDialog}
        onClickCancel={onClickCancelUpgradePlan}
      />
    </Page>
  );
}

function groupBy(objectArray: any[], property: string) {
  return objectArray.reduce(function (acc, obj) {
    let key = obj[property];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
}
